import { MinusCircle } from "lucide-react";
import { loadHome } from "../homeResource";
import { AskTextInput } from "../components/AskTextInput";
import { completedMentionParts } from "../lib/completedMentionParts";
import { mentionFragment } from "../lib/mentionFragment";
import { useAskMentions } from "../lib/useAskMentions";
import type { AskMention } from "../../../../backend/src/shared/askMentions";
/**
 * The Brief — the console's Home, in the settled layout from the
 * "Right pane edge options" design (option B — flush pane, centred 900px
 * column, 72px gutters, red for the worst only, empty blocks hidden).
 *
 * Its question is unchanged: *is the company actually using LedAI* — the
 * adoption blocks — and then *what is stuck*. What the design settled is the
 * SHAPE of the answer:
 *
 *  - The pane is flush (no card frame); content lives in a centred column
 *    (`WRAP`) and the page's only scrollbar is this body's own, with a
 *    stable gutter so nothing shifts when it appears.
 *  - The head is the greeting alone, set 25/800, with the generated-at crumb
 *    under it in the mono meta voice.
 *  - The stat cards, the TODAY banner, the roster and the coverage rows are
 *    `AdoptionPanels`' (restyled there); this view adds the "items past due"
 *    stat to that grid, because the grid is one read and the numbers come
 *    from two queries.
 *  - Past due and Moved pair into the design's two columns: a `colhead`
 *    (mono label, 19px sentence with the gold-underlined link, mono subline)
 *    over a bordered well of rows — age · kind chip · title (· Nudge).
 *    RED IS FOR THE WORST ONLY: the first past-due row's age is red, every
 *    other age is the sub tone (`redUse: "Worst only"`).
 *  - Lists are capped with an explicit "All N …" link instead of a nested
 *    scroll region — the only scrollbar on screen is the pane's own.
 *
 * ## Every number here is still a link
 *
 * Spec §2.1 survives the restyle. Stat tiles keep their server-named
 * targets; "past due" (the underline) and "All N stalled items" open the
 * item list with `stalledOnly=true`; each row opens its item; Nudge opens
 * the sheet.
 *
 * ## What was REMOVED stays removed — and what came back, changed
 *
 * Ball-in-court renders nowhere (spec §4; `noBallAffordance.test.ts`): no
 * raw holder string, no "Unassigned", no per-row pills. The design's
 * "By trade / subs behind" column IS here (user-directed, 2026-08-30), but
 * it is a different claim: past-due items whose assignee actually RESOLVED
 * — to a directory person, an email, or a named party — regrouped by the
 * COMPANY they work for, with every total traceable to the items under it.
 * Rows with nobody on them stay out; absence is never a bucket. The
 * review-count panel stays gone (spec §2.3).
 *
 * ## The headline is DERIVED, never authored
 *
 * The design writes "74 items are past due." — a sentence composed from the
 * same number the well below it renders, so the two cannot disagree. That is
 * the old headline's contract with the design's copy; `headline()` is still
 * the one place the sentence is built.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  type HomeResponse,
  fetchReviewQueue,
  type ApiError,
  type OrgMemberRow,
  type BriefResponse,
  type ConsoleRow,
  type ConsoleSite,
  type ReviewQueueResponse,
  type SubsBehindCompanyRow,
  type WalksResponse,
  type ProjectCardsResponse,
} from "../api";
import { useResource, useResourceCache, type Resource } from "../useResource";
import { homeQuery } from "../resourceCache";
import { resourceQuery } from "../resourceCache";
import { H } from "../tokens";
import { HomeProjects } from "./home/HomeProjects";
import { HomeWalks } from "./home/HomeWalks";
import { HomeEmpty, NewProjectNote } from "./home/HomeEmpty";
import { SharePopover, ShareAvatars, useShareRoster } from "./home/SharePopover";
import {
  recentProjects,
} from "./home/widgetData";
import type { PickerProject } from "../shell/projectPins";

import { consoleGreeting, greetingName, projectGreeting } from "../greeting";
import { resolveIdentity, useAuthPerson, type AuthPerson } from "../auth/identity";
import { AdoptionPanels, type AdoptionLinks, type BriefStatCard } from "./AdoptionPanels";
import { SAVED_VIEWS } from "./ItemList";
import { C, FONT_MONO } from "../tokens";
import { DictationButton } from "../components/DictationButton";
import { CHAT_INPUT_STYLE, CHAT_SEND_STYLE } from "../components/ChatInputChrome";
import {
  ASK_COMPOSER_CLEARANCE,
  Card,
  ConsoleViewProps,
  EmptyPanel,
  FailurePanel,
  KIND_ICON,
  KIND_LABEL,
  KIND_LABEL_PLURAL,
  KindChip,
  KindGlyph,
  LoadingPanel,
  META,
  QuietLine,
  ROW_TITLE,
  StaleBanner,
  ageOf,
  clockOf,
  dayLabel,
  plural,
  whenStamp,
} from "../components/chrome";
import { NudgeSheet } from "../components/NudgeSheet";
import { ProjectStrip } from "../components/ProjectStrip";
import { SkeletonCard, RecentWalksSkeleton, ProjectsSkeleton } from "../components/Skeleton";
import { HOME_TOP } from "../layout";
import { type HomePanel } from "./HomeCarousel";
import { Lockup } from "../components/Lockup";
import {
  ASK_ATTACHMENT_ACCEPT,
  AskAttachmentError,
  readAskAttachment,
  type AskAttachment,
} from "../lib/askAttachment";
import { holdAskAttachment } from "../lib/askHandoff";

// ---------------------------------------------------------------------------
// The settled column (design option B)
// ---------------------------------------------------------------------------

/** The content column: 900px outer box; the gutters ride on `.pagecol`
 *  (72px, 20px under the design's 820px breakpoint — see `main.tsx`). */
const WRAP: React.CSSProperties = {
  // LED-1151: Home's column is the design's 1040px (`mainMaxW`), and its
  // blocks carry their own top margins (34 / 22 / 34), so no flex gap.
  maxWidth: 1040,
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
};

/**
 * How many rows a capped list shows before its "All N …" link.
 *
 * THREE (user-directed, 2026-09-07). Two panels open side by side now, and at
 * five rows each the pair ran past the fold — so the reader scrolled to reach
 * "Recent walks" through a list they had already decided to open in full or
 * not. Three is enough to show the shape of a list and its worst row, which
 * is all a panel on Home is for; the link under it goes to the whole thing.
 */
const LIST_VISIBLE_ROWS = 3;

/** The id the "subs behind" stat card scrolls to. */
const SUBS_BEHIND_ANCHOR_ID = "brief-subs-behind";

/** The design's two work columns. `auto-fit` collapses to one column when
 *  the window cannot hold two — the design's own 1000px media query, as a
 *  track constraint instead of a breakpoint. */
const COLS: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
  gap: 28,
  minWidth: 0,
  alignItems: "start",
};

/** The design's bordered well — the container every row list sits in. */
const WELL: React.CSSProperties = {
  border: `1px solid ${C.line}`,
  borderRadius: 16,
  background: C.surface,
  boxShadow: `0 1px 2px ${C.line}`,
  minWidth: 0,
  overflow: "hidden",
};

/** The "All N …" row under a capped well. */
const MORE_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  padding: "11px 0",
  fontSize: 14.37,
  fontWeight: 600,
};

/**
 * A console URL with SEVERAL parameters rewritten and the rest preserved.
 *
 * The shell's `hrefWith` does one at a time and is not importable here
 * without a cycle (the shell imports this view). The rule it exists to
 * enforce is what matters and is kept: every link starts from the CURRENT
 * `search`, so `?workspace=` and everything else rides along. A link that
 * spelled its own query from scratch would silently answer for a different
 * tenant than the page claims.
 */
function hrefWithAll(search: string, entries: Array<[string, string | null]>): string {
  // Never parse a pathname as a query — see `Shell.tsx`'s `queryOf`.
  const at = search.indexOf("?");
  const rawQuery = at >= 0 ? search.slice(at + 1) : search.startsWith("/") ? "" : search;
  const params = new URLSearchParams(rawQuery);
  for (const [key, value] of entries) {
    if (value === null) params.delete(key);
    else params.set(key, value);
  }
  const query = params.toString();
  return query ? `?${query}` : location.pathname;
}

type HomeAskMenu = "project" | "range" | null;

/** How many projects the Home scope menu lists before the box has to narrow
 *  them. Five fills the menu without outgrowing the screen. */
const PROJECT_MENU_ROWS = 5;

const ASK_RANGES = [
  ["all", "All time"],
  ["today", "Today"],
  ["week", "This week"],
  ["month", "Last 30 days"],
] as const;

function labelFor(
  options: readonly (readonly [string, string])[],
  value: string | null,
  fallback: string,
) {
  return options.find(([id]) => id === value)?.[1] ?? fallback;
}


/**
 * The ONE set of text metrics the command bar's two layers share.
 *
 * The bar is a transparent `<input>` with a styled copy of its own value
 * painted underneath — that is how a mention gets its own colour inside a
 * plain text field. The caret, though, is drawn by the input, at the input's
 * text positions. So the moment the two layers measure text differently the
 * caret stops landing where the reader can see their text.
 *
 * It did. The input's style ended in `font: "inherit"`, and `font` is a
 * SHORTHAND — it reset the `fontSize: 16` declared four lines above it back
 * to the inherited 14. A 14px input under a 16px overlay drifts about a
 * character every eight, so by the end of a sentence the caret sat well
 * inside the text. The input also carried the UA's default `padding: 1px 2px`
 * against an overlay pinned to `inset: 0`, for another two.
 *
 * Hence one object, spread into both, rather than two lists that agree today.
 * Anything that changes glyph advance has to live here: family, size,
 * WEIGHT, letter-spacing, padding.
 */
const ASK_TEXT: React.CSSProperties = {
  fontFamily: "inherit",
  fontSize: 18.4,
  fontWeight: 400,
  letterSpacing: "normal",
  lineHeight: "22px",
  padding: 0,
  margin: 0,
  border: 0,
};

/**
 * The Home command bar's scope controls. They deliberately use the same
 * query-string state as the rest of the console: a scope survives a refresh,
 * a pasted link, and the handoff from Home into the full Ask Led screen.
 */
function HomeAskFilters({
  sites,
  activeSiteId,
  range,
  attachment,
  attachmentError,
  onAttach,
  onRemoveAttachment,
  onSet,
  share,
}: {
  /** A scoped Home's people + Share control, between the project and the
   *  time range (LED-1151). */
  share?: React.ReactNode;
  sites: ConsoleSite[];
  activeSiteId: string | null;
  range: string | null;
  /** The file waiting to go with the next question, if any. */
  attachment: AskAttachment | null;
  attachmentError: string | null;
  onAttach: (file: File) => void | Promise<void>;
  onRemoveAttachment: () => void;
  onSet: (key: "site" | "askRange", value: string | null) => void;
}) {
  const [open, setOpen] = useState<HomeAskMenu>(null);
  const [projectQuery, setProjectQuery] = useState("");
  const wrap = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = () => {
      setOpen(null);
      setProjectQuery("");
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const projectNeedle = projectQuery.trim().toLowerCase();
  const matchedSites = projectNeedle
    ? sites.filter((site) => (site.name || site.id).toLowerCase().includes(projectNeedle))
    : sites;
  const activeProject = sites.find((site) => site.id === activeSiteId);
  const projectLabel = activeProject?.name || activeProject?.id || "All projects";
  const rangeLabel = labelFor(ASK_RANGES, range, "All time");
  const choose = (key: "site" | "askRange", value: string | null) => {
    setOpen(null);
    // The filter is per-OPENING. A menu that reopened still narrowed by what
    // was typed last time would look like it had lost projects.
    setProjectQuery("");
    onSet(key, value);
  };

  const FilterIcon = ({ kind }: { kind: "pin" | "calendar" }) => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      {kind === "pin" ? (
        <>
          <path d="M12 21s6.5-6 6.5-10.5a6.5 6.5 0 1 0-13 0C5.5 15 12 21 12 21z" stroke={C.sub} strokeWidth="1.7" />
          <circle cx="12" cy="10.4" r="2.2" stroke={C.sub} strokeWidth="1.7" />
        </>
      ) : (
        <>
          <rect x="3.5" y="5" width="17" height="15" rx="2.5" stroke={C.sub} strokeWidth="1.7" />
          <path d="M7.5 3v4M16.5 3v4M3.5 10h17" stroke={C.sub} strokeWidth="1.7" strokeLinecap="round" />
        </>
      )}
    </svg>
  );

  const Chevron = () => (
    <svg width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden>
      <path d="m2 1 5 5-5 5" stroke={C.sub2} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const menuStyle: React.CSSProperties = {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    zIndex: 20,
    minWidth: 210,
    padding: 6,
    background: C.surface,
    border: `1px solid ${C.line2}`,
    borderRadius: 14,
    boxShadow: `0 12px 30px ${C.line}`,
  };
  const optionStyle = (selected: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    width: "100%",
    minHeight: 36,
    padding: "7px 10px",
    border: 0,
    borderRadius: 9,
    background: selected ? C.railRow : "transparent",
    color: C.ink,
    cursor: "pointer",
    font: "inherit",
    fontSize: 14.95,
    fontWeight: selected ? 650 : 500,
    textAlign: "left",
  });
  const triggerStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    minHeight: 36,
    padding: "0 8px",
    border: 0,
    borderRadius: 10,
    background: "transparent",
    color: H.ink,
    cursor: "pointer",
    font: "inherit",
    fontSize: 14,
    fontWeight: 700,
    whiteSpace: "nowrap",
  };

  const Trigger = ({
    id,
    label,
    icon,
  }: {
    id: Exclude<HomeAskMenu, null>;
    label: string;
    icon?: React.ReactNode;
  }) => (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        className="kb home-scope-filter"
        aria-haspopup="menu"
        aria-expanded={open === id}
        onClick={() => setOpen(open === id ? null : id)}
        style={triggerStyle}
      >
        {icon}
        <span>{label}</span>
        <Chevron />
      </button>
      {open === id && (
        <div role="menu" className="home-scope-menu" style={menuStyle}>
          {id === "project" && (
            <>
              {/* Scope is reversible in the same place it is chosen. Without
                  this row, selecting one project stranded Home in that scope
                  until the reader found the separate project-card control. */}
              <button
                type="button"
                role="menuitemradio"
                aria-checked={activeSiteId === null}
                className="kb"
                data-testid="home-ask-all-projects"
                style={optionStyle(activeSiteId === null)}
                onClick={() => choose("site", null)}
              >
                All projects
              </button>
              {/* Keep search above a scrollable list with five visible rows. */}
              {sites.length > PROJECT_MENU_ROWS && (
                <input
                  autoFocus
                  data-testid="home-ask-project-search"
                  aria-label="Find a project"
                  value={projectQuery}
                  placeholder="Find a project"
                  onInput={(event) => setProjectQuery((event.target as HTMLInputElement).value)}
                  onChange={(event) => setProjectQuery(event.target.value)}
                  style={{
                    width: "calc(100% - 12px)",
                    boxSizing: "border-box",
                    margin: "2px 6px 6px",
                    padding: "7px 9px",
                    border: `1px solid ${C.line2}`,
                    borderRadius: 9,
                    outline: "none",
                    background: C.inset,
                    color: C.ink,
                    font: "inherit",
                    fontSize: 14.95,
                  }}
                />
              )}
              <div
                data-testid="home-ask-project-list"
                style={{ maxHeight: PROJECT_MENU_ROWS * 36, overflowY: "auto" }}
              >
              {matchedSites.map((site) => (
                <button key={site.id} role="menuitemradio" aria-checked={site.id === activeSiteId} className="kb" style={{ ...optionStyle(site.id === activeSiteId), height: 36 }} onClick={() => choose("site", site.id)}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {site.name || site.id}
                  </span>
                </button>
              ))}
              {matchedSites.length === 0 && (
                <span style={{ display: "block", fontSize: 14.37, color: C.sub, padding: "8px 10px" }}>
                  No project matches that.
                </span>
              )}
              </div>
            </>
          )}
          {id === "range" && ASK_RANGES.map(([value, label]) => (
            <button key={value} role="menuitemradio" aria-checked={(range ?? "all") === value} className="kb" style={optionStyle((range ?? "all") === value)} onClick={() => choose("askRange", value)}>{label}</button>
          ))}
        </div>
      )}
    </span>
  );

  return (
    <div ref={wrap} data-testid="home-ask-filters" style={{ position: "relative", width: "100%", maxWidth: 780, boxSizing: "border-box", padding: "0 10px", display: "flex", alignItems: "center", gap: 8, marginTop: 14, color: C.sub }}>
      <span style={{ position: "relative", display: "inline-flex", height: 36, alignItems: "center" }}>
        <button type="button" className="kb" aria-label="Attach a file" data-testid="home-ask-attach" onClick={() => { setOpen(null); fileRef.current?.click(); }} style={{ ...triggerStyle, width: 36, height: 36, justifyContent: "center", padding: 0, lineHeight: 1 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          </svg>
        </button>
        {/* The input itself is never seen: a bare file control cannot be
            styled to look like anything else, and the plus button above is the
            affordance. `value` is cleared on every change so picking the SAME
            file twice still fires. */}
        <input
          ref={fileRef}
          type="file"
          accept={ASK_ATTACHMENT_ACCEPT}
          data-testid="home-ask-file"
          style={{ display: "none" }}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void onAttach(file);
          }}
        />
      </span>
      <Trigger id="project" label={projectLabel} icon={<FilterIcon kind="pin" />} />
      {share && (
        <>
          <span style={{ flex: 1 }} />
          {share}
        </>
      )}
      <span style={{ width: 1, height: 20, background: C.line, flexShrink: 0 }} />
      {/* No SOURCE control. It offered "Walks + photos / Walks / Photos / All
          records", and Ask Led AI reads whatever answers the question — so
          the picker narrowed nothing the reader could observe and implied a
          precision the answer does not have (user-directed, 2026-09-07). */}
      <Trigger id="range" label={rangeLabel} icon={<FilterIcon kind="calendar" />} />
      <span style={{ flex: 1 }} />

      {/* What is attached, and how to take it off. A file the reader cannot
          see is a file they cannot remove. */}
      {attachment && (
        <span
          data-testid="home-ask-attachment"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            maxWidth: 260,
            padding: "5px 6px 5px 10px",
            borderRadius: 999,
            background: C.faint,
            border: `1px solid ${C.hair}`,
            fontSize: 14.37,
            color: C.ink,
          }}
        >
          <span style={{ ...ROW_TITLE, minWidth: 0 }}>{attachment.name}</span>
          {/* Said, not hidden: this console re-encoded the reader's file, and
              that is the one place it changes what they picked. */}
          {attachment.downscaled && (
            <span style={{ ...META, fontSize: 9.77, color: C.sub2, flexShrink: 0 }}>resized</span>
          )}
          <button
            type="button"
            className="kb"
            aria-label={`Remove ${attachment.name}`}
            onClick={onRemoveAttachment}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              width: 18,
              height: 18,
              padding: 0,
              border: "none",
              borderRadius: 999,
              background: C.line2,
              cursor: "pointer",
            }}
          >
            <MinusCircle size={16} color={C.sub} aria-hidden />
          </button>
        </span>
      )}
      {attachmentError && (
        <span
          data-testid="home-ask-attachment-error"
          style={{ fontSize: 14.37, color: C.red, maxWidth: 320, lineHeight: 1.4 }}
        >
          {attachmentError}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The Home head — the console redesign's centred column
// ---------------------------------------------------------------------------

/**
 * Which panels a pill opens.
 *
 * One panel each, except past due — where "who is behind" is the same
 * question as "what is late", read the other way round, so the two belong
 * open together. Records and closed-out used to share a pill and were two
 * unrelated readings stacked; closed-out has its own now (user-directed,
 * 2026-09-07).
 */
export type HomePanelKey = "review" | "pastDue" | "records" | "closedout";

const PANEL_KEYS: Record<HomePanelKey, string[]> = {
  review: ["review"],
  pastDue: ["pastdue", "subs"],
  records: ["records"],
  closedout: ["closedout"],
};

/**
 * One of the three numbers over the fold — a pill that opens a panel rather
 * than a link that leaves the page.
 *
 * The design draws the count heavy, the noun light, and a chevron that
 * becomes a close mark while the panel is open. The dot is a status colour
 * where the number has one (red past due, green walks) and absent where it
 * does not: a dot beside every pill would be decoration.
 */
function HomePill({
  count,
  label,
  dot,
  open = false,
  onToggle,
  onGo,
  testId,
}: {
  count: number;
  label: string;
  dot?: string;
  /** Expanding pills only. */
  open?: boolean;
  onToggle?: () => void;
  /** A pill that NAVIGATES instead of expanding — it draws an arrow, and it
   *  is not an `aria-expanded` control, because it does not expand. */
  onGo?: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      className="kb"
      data-testid={testId}
      data-open={open ? "true" : "false"}
      aria-expanded={onGo ? undefined : open}
      onClick={onGo ?? onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        height: 34,
        padding: "0 14px 0 12px",
        borderRadius: 999,
        background: open ? C.surface : C.railRow,
        border: `1px solid ${open ? C.line2 : C.hair}`,
        boxShadow: open
          ? `0 1px 2px ${C.softShadow}, 0 3px 8px ${C.softShadowLow}`
          : undefined,
        fontSize: 14.95,
        color: open ? C.ink : C.sub,
        cursor: "pointer",
        font: "inherit",
      }}
    >
      {dot && (
        <span
          aria-hidden
          style={{ width: 6, height: 6, borderRadius: 3, background: dot, flexShrink: 0 }}
        />
      )}
      <span style={{ fontWeight: 600, color: C.ink }}>{count}</span>
      {label}
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke={C.sub2}
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden
        style={{ flexShrink: 0 }}
      >
        {onGo ? (
          <path d="M4 12h15M13 6l6 6-6 6" />
        ) : open ? (
          <path d="M6 6l12 12M18 6L6 18" />
        ) : (
          <path d="M9 5l7 7-7 7" />
        )}
      </svg>
    </button>
  );
}

/**
 * The greeting.
 *
 * Time of day and the reader's name — see `src/client/greeting.ts` for why
 * the console stopped using the phone's nine-rung ladder. The hour table
 * lives THERE, not here: this file had its own once, and that is how the
 * console and the phone came to disagree in the first place.
 *
 * `generatedAt`, not `new Date()`, so the greeting and the numbers under it
 * are as of the same moment.
 */
function greeting(_data: BriefResponse, generatedAt: Date, name: string, project?: string | null): string {
  return project ? projectGreeting(project, name) : consoleGreeting({ generatedAt, name });
}

/**
 * The first name to greet, from whichever source can actually name the person.
 *
 * This used to be `greetingName(data.caller?.name)` — the MIRROR alone — and
 * the greeting was nameless for every real account as a result. `caller.name`
 * comes from the Mongo `users` row, which only `upsertUserFromClerk` ever
 * writes, so on these accounts it carries a `userId` and a `clerkUserId` and
 * nothing else. Meanwhile the sidebar three inches away has been printing the
 * person's real name the whole time, because it asks `resolveIdentity` —
 * Clerk first, mirror second. Two surfaces, one signed-in person, and only
 * one of them knew who it was.
 *
 * `source === "name"` is the load-bearing half. `resolveIdentity` falls back
 * to an email and then to a raw user id so the FOOTER always has something to
 * print, and it labels which one it got — but a greeting is not a footer. Its
 * first word is a name or it is nothing: `pickGreeting` already drops the name
 * line when handed `""`, and "Good evening, user_31Kd…" is worse than "Good
 * evening." So anything that is not a name is passed through as no name.
 */
function greetingNameFor(
  person: AuthPerson | null,
  caller: BriefResponse["caller"],
): string {
  const identity = resolveIdentity(person, caller);
  return identity.source === "name" ? greetingName(identity.label) : "";
}

/**
 * The past-due sentence, in the design's copy — composed from the same
 * number the well under it renders, so the two cannot disagree.
 *
 * Returns pieces rather than a string so the underlined phrase can be the
 * link the design draws (3px gold underline): it opens the item list with
 * `stalledOnly=true`, which is the list the sentence is counting.
 */
export function headline(
  brief: BriefResponse,
  /** The clock the day count is read against — `ageOf`'s, by default. */
  now: number = Date.now(),
): {
  before: string;
  /** The gold-underlined link text, absent when there is nothing to open. */
  anchorText?: string;
  after: string;
  /** The mono subline under the sentence, or `undefined` for none. */
  subline?: string;
} {
  const stalled = brief.stalled;
  const count = brief.stalledCount ?? stalled.length;
  const generated = brief.generated?.total ?? 0;

  if (stalled.length > 0) {
    const oldest = stalled[0]!;
    const days = wholeDaysSince(oldest.lastMovementAt, oldest.daysSinceMovement, now);
    return {
      before: `${plural(count, "item is", "items are")} `,
      anchorText: "past due",
      after: ".",
      subline: `Longest first · oldest has not moved in ${plural(days, "day", "days")}`,
    };
  }

  if (generated > 0) {
    return {
      before: `Nothing is past due. ${plural(
        generated,
        "record has",
        "records have",
      )} been generated from walks in this workspace.`,
      after: "",
    };
  }

  return {
    before: "Nothing is past due, and no records have been generated yet.",
    after: "",
  };
}

// ---------------------------------------------------------------------------
// Column heads and rows — the design's `colhead`, `well` and `item`
// ---------------------------------------------------------------------------

function ColHead({
  label,
  sentence,
  subline,
}: {
  /**
   * The eyebrow. OMITTED for a carousel panel: the tab strip above it already
   * names the panel, and the design hides the panel's own eyebrow for exactly
   * that reason (`.panel .hidden-eyebrow`). Passed only where a colhead
   * stands on its own.
   */
  label?: string;
  sentence: React.ReactNode;
  subline?: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
      {label && (
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ ...META, color: C.sub2 }}>{label}</span>
        </span>
      )}
      <h3
        style={{
          fontSize: 21.85,
          fontWeight: 700,
          letterSpacing: "-0.4px",
          lineHeight: 1.3,
          margin: 0,
        }}
      >
        {sentence}
      </h3>
      {subline && <span style={{ ...META, color: C.sub }}>{subline}</span>}
    </div>
  );
}

/**
 * One past-due row, in the design's `item` anatomy: the age, the kind chip,
 * the truncating title, and Nudge. The age is red on the WORST row only
 * (`worst`) — the list arrives longest-first, so that is row zero.
 */
function PastDueRow({
  row,
  worst,
  first,
  href,
  onOpen,
  onNudge,
}: {
  row: ConsoleRow;
  worst: boolean;
  first: boolean;
  href: string;
  onOpen: () => void;
  onNudge: () => void;
}) {
  const age = ageOf(row);
  return (
    <div
      data-testid="stalled-row"
      data-item-id={row.id}
      // One line by default; the TITLE takes its own wrapping line once the
      // panel itself is under 400px wide — a carousel panel one-up on a phone,
      // or two-up in a narrow window. That is a question about the PANEL, not
      // the viewport, so it is a container query (`main.tsx`), and this row
      // carries the classes it keys on.
      className="brief-item-row"
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        padding: "13px 16px",
        borderTop: first ? undefined : `1px solid ${C.line}`,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 14.95,
          fontWeight: 700,
          // NOT red. The design's own saved state uses red for nothing on this
          // page (`redUse: "None"` — its `.age.worst` is `sub`), and the list
          // is longest-first anyway, so the worst row is the top one: the
          // ORDER says it, and a red number said it twice.
          color: C.sub,
          flexShrink: 0,
          minWidth: 34,
        }}
      >
        {age.text}
      </span>
      <KindChip kind={row.kind} />
      <a
        href={href}
        data-testid="row-title"
        className="brief-item-title"
        style={{ ...ROW_TITLE, fontSize: 15.52, fontWeight: 600, flex: 1 }}
        onClick={(event) => {
          event.preventDefault();
          onOpen();
        }}
        title={row.title}
      >
        {row.title}
      </a>
      {/* Nudge is offered on every past-due row. The sheet itself states who
          it could and could not address (`nudge-note`), which is the honest
          place for that fact. */}
      <button
        onClick={onNudge}
        data-testid="nudge-button"
        data-item-id={row.id}
        className="brief-item-right"
        style={{
          flexShrink: 0,
          fontSize: 14.37,
          fontWeight: 700,
          color: C.amber,
          whiteSpace: "nowrap",
        }}
      >
        Nudge
      </button>
    </div>
  );
}

const DAY_MS = 86_400_000;

/**
 * Whole days since a row last moved, ROUNDED DOWN — so a count printed in days
 * never claims a row is older than it is. The day-granular sibling of `ageOf`
 * (`components/chrome.tsx`), and read from the same place for the same reason.
 *
 * It reads the `lastMovementAt` INSTANT, not `daysSinceMovement`. That count
 * is not the raw age: the projection has already quantized it to a tenth of a
 * day with `Math.round` (`consoleItem.ts`, `roundDays`), half of which rounds
 * UP. `Math.round` over it — what every call site here used to do — made a
 * 1.6-day row "2 days" old; and even flooring it cannot help a row that
 * arrives already inflated, like a 1.96-day row sent as `2.0`.
 *
 * The count is the fallback only when there is no readable instant (a
 * hand-built row: the projection always sends one), and it is floored there
 * too. That fallback can still over-read by the quantization — up to 1.2h
 * across a day boundary — which is why it is the fallback. Never negative: an
 * instant a skewed clock puts in the future reads as none elapsed.
 *
 * `ItemDetail` imports this rather than keeping a second spelling of "how many
 * days old is this row" that could disagree with the first.
 */
export function wholeDaysSince(
  lastMovementAt: string | undefined,
  daysSinceMovement: number,
  now: number = Date.now(),
): number {
  const movedAt = Date.parse(lastMovementAt ?? "");
  const days = Number.isFinite(movedAt) ? (now - movedAt) / DAY_MS : daysSinceMovement;
  return Math.max(0, Math.floor(days));
}

/** "today" / "1 day ago" / "N days ago" — the Closed-out rows' clock. "today"
 *  means under a whole day, which is what it always meant; see
 *  `wholeDaysSince` for why the count is read from the instant. */
function relativeDays(
  row: Pick<ConsoleRow, "lastMovementAt" | "daysSinceMovement">,
  now: number = Date.now(),
): string {
  const days = wholeDaysSince(row.lastMovementAt, row.daysSinceMovement, now);
  if (days < 1) return "today";
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

/**
 * One row that closed out recently — the design's anatomy: the kind, the
 * title, and a GREEN relative time on the right. No red and no Nudge: this
 * row is the evidence nobody has gone quiet.
 */
function MovedItemRow({
  row,
  first,
  href,
  onOpen,
}: {
  row: ConsoleRow;
  first: boolean;
  href: string;
  onOpen: () => void;
}) {
  return (
    <div
      data-testid="moved-row"
      data-item-id={row.id}
      className="brief-item-row"
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        padding: "13px 16px",
        borderTop: first ? undefined : `1px solid ${C.line}`,
        minWidth: 0,
      }}
    >
      <KindChip kind={row.kind} />
      <a
        href={href}
        data-testid="row-title"
        className="brief-item-title"
        style={{ ...ROW_TITLE, fontSize: 15.52, fontWeight: 600, flex: 1 }}
        onClick={(event) => {
          event.preventDefault();
          onOpen();
        }}
        title={row.title}
      >
        {row.title}
      </a>
      <span
        className="brief-item-right"
        style={{
          marginLeft: "auto",
          flexShrink: 0,
          fontFamily: FONT_MONO,
          fontSize: 13.23,
          fontWeight: 700,
          color: C.green,
          whiteSpace: "nowrap",
        }}
      >
        {relativeDays(row)}
      </span>
    </div>
  );
}

/** The "All N …" link under a capped well (the design's `.row.more`). */
function MoreLink({
  label,
  href,
  onOpen,
  testId,
}: {
  label: string;
  href: string;
  onOpen: () => void;
  testId: string;
}) {
  return (
    <div style={MORE_ROW}>
      <a
        href={href}
        data-testid={testId}
        onClick={(event) => {
          event.preventDefault();
          onOpen();
        }}
        style={{ color: C.amber }}
      >
        {label}
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// By trade — the subs-behind column (the design's `subrow` anatomy)
// ---------------------------------------------------------------------------

/**
 * One company's pile: name, count, the share bar, and the sites-·-worst meta
 * line. The row is a BUTTON — clicking it opens the pile's own items, which
 * is the traceability the aggregate was built to preserve: every number up
 * top is re-derivable from the rows that unfold under it.
 *
 * The TOP bar is amber and every other bar is the dormant tone — the same
 * worst-only signal rule as the past-due ages (`redUse: "Worst only"`).
 *
 * Exported for its test only: the panel it sits in opens from no Home pill
 * today, so rendering the Brief cannot reach it.
 */
export function SubsBehindRow({
  pile,
  top,
  first,
  share,
  siteNameOf,
  hrefForItem,
  onOpenItem,
  movedAtOf,
}: {
  pile: SubsBehindCompanyRow;
  top: boolean;
  first: boolean;
  /** Bar width, 0..1 of the card — count over the biggest pile, scaled to
   *  the design's 78% ceiling. */
  share: number;
  siteNameOf: (jobSiteId: string) => string | undefined;
  hrefForItem: (itemId: string) => string;
  onOpenItem: (itemId: string) => void;
  /**
   * An item's `lastMovementAt`, by id. A pile item carries only the quantized
   * day count, but every one of them IS a row of the same response's
   * `stalled` list (`subsBehind(stalled)`, `getBrief.ts`), which carries the
   * instant — so the ages are read from there. See `wholeDaysSince`.
   */
  movedAtOf: (itemId: string) => string | undefined;
}) {
  const [open, setOpen] = useState(false);
  // One clock for the company row and every item under it, so "Worst Nd"
  // is always the largest of the ages it unfolds into.
  const now = Date.now();
  const itemDays = (item: SubsBehindCompanyRow["items"][number]) =>
    wholeDaysSince(movedAtOf(item.itemId), item.daysSinceMovement, now);
  const worstDays = pile.items.length
    ? Math.max(...pile.items.map(itemDays))
    : wholeDaysSince(undefined, pile.worstDays, now);
  const sites = pile.jobSiteIds
    .slice(0, 2)
    .map((id) => siteNameOf(id) ?? id)
    .join(" · ");
  return (
    <div style={{ borderTop: first ? undefined : `1px solid ${C.line}` }}>
      <button
        data-testid="subs-behind-row"
        data-company={pile.company}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) auto",
          gap: "4px 12px",
          padding: "13px 16px",
          width: "100%",
          textAlign: "left",
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 15.52,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {pile.company}
        </span>
        <span
          style={{ fontFamily: FONT_MONO, fontSize: 13.8, fontWeight: 700, color: C.sub, whiteSpace: "nowrap" }}
        >
          {plural(pile.count, "item", "items")}
        </span>
        <span
          style={{
            gridColumn: "1 / -1",
            height: 4,
            borderRadius: 3,
            background: C.barEmpty,
            overflow: "hidden",
            display: "block",
          }}
        >
          <span
            data-testid="subs-behind-bar"
            style={{
              display: "block",
              height: "100%",
              width: `${Math.round(share * 100)}%`,
              // One muted fill for every company. The design's saved state
              // colours no bar (`redUse: "None"`, and its `.bar i.top` is the
              // same `dormant` as the rest): the WIDTH is the comparison, and
              // tinting the longest bar restated the thing the bar is for.
              background: C.dormant,
              borderRadius: 3,
            }}
          />
        </span>
        <span
          style={{
            gridColumn: "1 / -1",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            minWidth: 0,
          }}
        >
          <span
            style={{ ...META, color: C.sub2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {sites}
          </span>
          <span style={{ ...META, color: C.sub, whiteSpace: "nowrap" }}>
            {`Worst ${worstDays}d`}
          </span>
        </span>
      </button>

      {open && (
        <div data-testid="subs-behind-items" style={{ paddingBottom: 6 }}>
          {pile.items.map((item) => (
            <div
              key={item.itemId}
              data-testid="subs-behind-item"
              data-item-id={item.itemId}
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                padding: "7px 16px 7px 28px",
                minWidth: 0,
              }}
            >
              <span
                style={{ fontFamily: FONT_MONO, fontSize: 13.8, fontWeight: 700, color: C.sub, flexShrink: 0, minWidth: 30 }}
              >
                {`${itemDays(item)}d`}
              </span>
              <KindChip kind={item.kind} />
              <a href={hrefForItem(item.itemId)} data-testid="row-title" style={{ ...ROW_TITLE, fontSize: 14.37, fontWeight: 600, flex: 1 }}
                 onClick={(event) => { event.preventDefault(); onOpenItem(item.itemId); }} title={item.title}>{item.title}</a>
              <span
                style={{ ...META, fontSize: 9.77, fontWeight: 400, color: C.sub2, whiteSpace: "nowrap", flexShrink: 0 }}
                title="Who this item is sitting with"
              >
                {item.assignee}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The filtered-project walks list (personal workspaces)
// ---------------------------------------------------------------------------

/**
 * With the project filter on in a PERSONAL workspace, the primary question
 * is not "who walked" — there is only one walker — but "what have I walked
 * here". So the primary card beneath the stats is this project's walks
 * (user-directed, 2026-08-31), newest first, each opening its walk.
 */
/**
 * Recent walks — the design's three-up card grid at the foot of Home.
 *
 * The card leads with WHEN and how the walk synced, then its title, then the
 * records it produced, then one photo off it. That order is the reader's
 * question order: is this the walk I mean, did it land, what came out of it.
 *
 * Quiet on failure and skeletal while loading, like every other block on this
 * page: one card of a Home that has other things to say must not blank the
 * page (`AdoptionPanels`' rule).
 *
 * ## No photo on the card, unlike the design
 *
 * The design puts one of the walk's photos under the record list. `listWalks`
 * deliberately projects `mediaItems.type` and nothing else — "enough to count
 * presentable photos without pulling URLs" — and a photo needs a presigned R2
 * GET per walk. Adding that to a route the Walks page calls with `limit=200`
 * would mean 200 presigns per request to decorate three cards here. It wants
 * a bounded, opt-in field on the query rather than a quiet widening, so the
 * card carries the walk's facts and no tile until that exists.
 */
function RecentWalks({
  search,
  resource,
  hrefForWalk,
  onOpenWalk,
  onNavigate,
}: {
  search: string;
  resource: Resource<WalksResponse>;
  hrefForWalk: (visitId: string) => string;
  onOpenWalk: (visitId: string) => void;
  onNavigate: (href: string) => void;
}) {
  const { data, failure } = resource;
  const allWalksHref = hrefWithAll(search, [
    ["item", null],
    ["view", null],
    ["page", "walks"],
  ]);

  if (failure) return null;
  if (!data) return <RecentWalksSkeleton />;
  // The ARRAY, not the envelope. A response that arrived without `walks` is
  // not this block's to interpret, and reading `.length` off it is how a
  // block that is meant to stay quiet takes the page down instead.
  const recent = data.walks ?? [];
  if (recent.length === 0) return null;

  return (
    <div data-testid="recent-walks" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, padding: "0 2px" }}>
        <h2 style={{ margin: 0, fontSize: 19.55, fontWeight: 600, letterSpacing: "-0.25px" }}>
          Recent walks
        </h2>
        <span style={{ flex: 1 }} />
        <a
          className="kb"
          href={allWalksHref}
          onClick={(event) => {
            event.preventDefault();
            onNavigate(allWalksHref);
          }}
          style={{ fontSize: 14.95, fontWeight: 500, color: C.sub }}
        >
          {`All ${plural(data.total, "walk", "walks")}`}
        </a>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 18,
        }}
      >
        {recent.slice(0, 3).map((walk) => (
          <a
            key={walk.id}
            className="kb"
            data-testid="recent-walk-card"
            href={hrefForWalk(walk.id)}
            onClick={(event) => {
              event.preventDefault();
              onOpenWalk(walk.id);
            }}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 9,
              minWidth: 0,
              padding: "16px 16px 12px",
              borderRadius: 14,
              background: C.surface,
              border: `1px solid ${C.hair}`,
              boxShadow: `0 1px 2px ${C.softShadow}, 0 4px 12px ${C.softShadowLow}`,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ ...ROW_TITLE, fontSize: 14.37, color: C.sub2 }}>
                {whenStamp(walk.startedAt)}
              </span>
              <span style={{ flex: 1 }} />
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  flexShrink: 0,
                  ...META,
                  fontSize: 10.93,
                  color: C.sub,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    background:
                      walk.pendingReviewCount > 0
                        ? C.amber
                        : walk.itemCount > 0
                          ? C.green
                          : C.barEmpty,
                  }}
                />
                {walk.pendingReviewCount > 0
                  ? "Needs review"
                  : walk.itemCount > 0
                    ? "Reviewed"
                    : "No records"}
              </span>
            </span>
            <span
              style={{
                ...ROW_TITLE,
                fontSize: 17.83,
                fontWeight: 600,
                letterSpacing: "-0.25px",
                lineHeight: 1.3,
              }}
            >
              {walk.name || "Untitled walk"}
            </span>
            <span style={{ ...ROW_TITLE, fontSize: 14.95, color: C.sub }}>
              {[
                walk.recordedMinutes === undefined
                  ? null
                  : `${plural(walk.recordedMinutes, "min", "min")} recorded`,
                plural(walk.itemCount, "record", "records"),
                walk.photoCount ? plural(walk.photoCount, "photo", "photos") : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

/**
 * "Latest walk · N to review." — the design's review panel.
 *
 * The pill above it counts everything waiting; this names the ONE walk that
 * produced most of it and lists what came out. That is the design's own
 * logic, and it is the more useful half: a reviewer works a walk at a time,
 * so "13 waiting" is a number and "the 8:40a walk left you three" is a task.
 *
 * It reads the review queue rather than the Brief, because the Brief carries
 * `reviewCounts` (how many, by kind) and not the ROWS. Same endpoint the
 * Review page uses, so the two cannot disagree about what is pending.
 */
function LatestWalkToReview({
  search,
  refreshKey,
  onNavigate,
  hrefForItem,
  onOpenItem,
}: {
  search: string;
  refreshKey: number;
  onNavigate: (href: string) => void;
  hrefForItem: (id: string) => string;
  onOpenItem: (id: string) => void;
}) {
  const { data, failure } = useResource<ReviewQueueResponse>(
    `brief-review${search}`,
    () => fetchReviewQueue(search),
    refreshKey,
  );

  if (failure) return null;
  if (!data) return <SkeletonCard rows={3} rowHeight={34} />;

  const walk = (data.walks ?? [])[0];
  if (!walk) return null;

  // The newest walk's own pending rows, from the same grouping the Review
  // page draws. Four, like the design — a panel is a prompt to go and review,
  // not the review itself.
  const pending = (data.groups ?? [])
    .filter((group) => group.id === "not_reviewed")
    .flatMap((group) => group.sites.flatMap((site) => site.rows))
    .filter(
      (row) => (row.item.visitId ?? `no-walk:${row.item.jobSiteId}`) === walk.key,
    )
    .slice(0, LIST_VISIBLE_ROWS);
  if (pending.length === 0) return null;

  const reviewHref = hrefWithAll(search, [
    ["page", "review"],
    ["item", null],
    ["view", null],
    ["reviewing", walk.key],
  ]);

  return (
    <div style={{ minWidth: 0 }} data-testid="latest-walk-review">
      <ColHead
        subline={[
          walk.startedAt ? whenStamp(walk.startedAt) : null,
          walk.durationMinutes === undefined
            ? null
            : `${plural(walk.durationMinutes, "min", "min")}`,
        ]
          .filter(Boolean)
          .join(" · ")}
        sentence={
          <span data-testid="latest-walk-total">
            {"Latest walk · "}
            <span style={{ borderBottom: `3px solid ${C.gold}`, fontWeight: 700 }}>
              {`${walk.total} to review`}
            </span>
            .
          </span>
        }
      />
      <div style={WELL}>
        {pending.map((row, index) => (
          <a
            key={row.item.id}
            className="kb"
            data-testid="latest-walk-row"
            href={hrefForItem(row.item.id)}
            onClick={(event) => {
              event.preventDefault();
              onOpenItem(row.item.id);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 12px",
              borderTop: index === 0 ? undefined : `1px solid ${C.hairRow}`,
              minWidth: 0,
            }}
          >
            <span
              aria-hidden
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                borderRadius: 5,
                border: `1px solid ${C.hair}`,
                background: C.surface,
                flexShrink: 0,
              }}
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.sub2}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d={KIND_ICON[row.item.kind].d1} />
                <path d={KIND_ICON[row.item.kind].d2} />
              </svg>
            </span>
            <span style={{ ...ROW_TITLE, flex: 1, fontSize: 14.95, fontWeight: 500 }}>
              {row.item.title}
            </span>
            <span
              style={{
                fontFamily: FONT_MONO,
                fontSize: 12.08,
                color: C.sub,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {KIND_LABEL[row.item.kind].toLowerCase()}
            </span>
          </a>
        ))}
      </div>
      <MoreLink
        label="Review this walk"
        href={reviewHref}
        onOpen={() => onNavigate(reviewHref)}
        testId="latest-walk-more"
      />
    </div>
  );
}

/**
 * Home's project strip (design 16b), with its own fetch.
 *
 * A route of its own, and therefore a fetch of its own: the strip's contents
 * are CEILING-wide because it is the control that changes `?site=`, while
 * everything else on this page honours that filter. Reading it off the Brief
 * payload would have meant one field in that payload quietly ignoring the
 * parameter the rest of it obeys.
 *
 * It renders NOTHING while loading and nothing on failure. A picker is
 * chrome, not content — a skeleton where the projects go would push the whole
 * page down and then let it snap back, and a failure panel here would tell
 * the reader their Home is broken when the only thing they have lost is a
 * shortcut the sidebar still offers.
 */
function HomeProjectStrip({
  resource,
  activeSiteId,
  onPick,
  hrefFor,
}: {
  resource: Resource<ProjectCardsResponse>;
  activeSiteId: string | null;
  onPick: (siteId: string | null) => void;
  hrefFor: (siteId: string | null) => string;
}) {
  const { data, loading } = resource;
  // Read defensively rather than trusting the shape. A picker is chrome: if
  // this route ever answers with something unexpected — a partial payload, an
  // older deployment, a proxy's error page rendered as JSON — the right
  // outcome is no strip, not a Home that throws and takes every panel on the
  // page down with it.
  if (!data && loading) return <ProjectsSkeleton />;
  const projects = data?.projects ?? [];
  if (projects.length === 0) return null;
  return (
    <ProjectStrip
      projects={projects}
      totals={
        data?.totals ?? {
          sites: projects.length,
          toReview: projects.reduce((sum, project) => sum + (project.toReview ?? 0), 0),
          pastDue: projects.reduce((sum, project) => sum + (project.pastDue ?? 0), 0),
        }
      }
      activeSiteId={activeSiteId}
      onPick={onPick}
      hrefFor={hrefFor}
    />
  );
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

export function Brief(props: ConsoleViewProps) {
  const {
    search,
    refreshKey,
    personalHref,
    onGoPersonal,
    hrefForItem,
    onOpenItem,
    onNavigate,
    hrefForProject,
    onOpenProject,
    hrefForWalk,
    onOpenWalk,
  } = props;
  const [nudgeItemId, setNudgeItemId] = useState<string | null>(null);
  // Home is a genuine Ask entry point, not a decorative link. Keeping the
  // draft here means typing is never lost while a filter menu is opened.
  const [homeQuestion, setHomeQuestion] = useState("");
  /** The file waiting to go with the next question — see `askHandoff`. */
  const [askAttachment, setAskAttachment] = useState<AskAttachment | null>(null);
  const [askAttachmentError, setAskAttachmentError] = useState<string | null>(null);
  const [mentionCaret, setMentionCaret] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionDismissed, setMentionDismissed] = useState(false);
  const [completedMentions, setCompletedMentions] = useState<string[]>([]);
  const homeAskInput = useRef<HTMLTextAreaElement>(null);
  /** The By-trade cap's "All N subs" toggle — a reading convenience, local
   *  on purpose (same reasoning as the coverage cap's). */
  const [allSubs, setAllSubs] = useState(false);
  /** Summary pills start unselected in both personal and team workspaces. */
  const [panelKey, setOpenPanel] = useState<HomePanelKey | null>(null);
  /** Clerk's live profile — the source that actually knows the person's name.
   *  A hook, so it is read here and passed down; `greeting` is a plain
   *  function and must stay one (it is called from JSX below a `return`). */
  const person = useAuthPerson();

  // A project or period narrows the SAME Home. A workspace is a security
  // boundary, so its previous Home must never remain visible for even one
  // frame while the next workspace loads. Keep the last rendered query in a
  // ref (rather than state) so deciding this does not itself add a render.
  const previousSearch = useRef(search);
  const keepHomeDuringScopeChange =
    new URLSearchParams(previousSearch.current).get("workspace") ===
    new URLSearchParams(search).get("workspace");
  useEffect(() => {
    previousSearch.current = search;
  }, [search]);

  const cache = useResourceCache();
  const home = useResource<HomeResponse>(
    `home${homeQuery(search)}`, () => loadHome(cache, search), refreshKey,
    { keepPreviousDataOnNavigation: keepHomeDuringScopeChange },
  );
  const { failure, refreshFailure } = home;
  const data = home.data?.brief ?? null;
  const projectResource: Resource<ProjectCardsResponse> = { ...home, data: home.data?.projects ?? null };
  const walksResource: Resource<WalksResponse> = { ...home, data: home.data?.walks ?? null };

  // Project tiles read the inventory `/api/home` already carries (the
  // same scoped projection the brief is built from), plus a wider workspace
  // walk list for the walk streak and the tiles' walk counts.
  const inventoryResource = { ...home, data: home.data?.inventory ?? null };
  const scopeSearch = resourceQuery(search, ["workspace"]);
  const streakWalksResource: Resource<WalksResponse> = {
    ...home, data: home.data?.streakWalks ?? home.data?.walks ?? null,
  };
  const scopedSite = new URLSearchParams(search).get("site");
  const [shareOpen, setShareOpen] = useState(false);
  const shareButton = useRef<HTMLButtonElement>(null);
  // The stagger replays every time the scope changes: the class NAME flips
  // a/b, so the animation restarts without remounting anything.
  const [animParity, setAnimParity] = useState(0);
  const lastScope = useRef(scopedSite);
  useEffect(() => {
    if (lastScope.current === scopedSite) return;
    lastScope.current = scopedSite;
    setAnimParity((parity) => (parity === 0 ? 1 : 0));
    setShareOpen(false);
  }, [scopedSite]);
  const roster = useShareRoster(scopeSearch, refreshKey, !!scopedSite);
  const [newProjectNote, setNewProjectNote] = useState(false);

  const activeFragment = mentionFragment(homeQuestion, mentionCaret, completedMentions);
  const mentionResults = useAskMentions(data?.sites ?? [], mentionDismissed ? null : activeFragment?.query ?? null, search, [
    ...(data?.subsBehind ?? []).map(company => company.company),
    ...[...(data?.stalled ?? []), ...(data?.moved ?? [])].map(row => row.assigneeCompany),
  ]);
  useEffect(() => { setMentionIndex(0); }, [activeFragment?.query, search]);
  useEffect(() => {
    document.getElementById(`home-ask-mention-${mentionIndex}`)?.scrollIntoView?.({ block: "nearest" });
  }, [mentionIndex]);

  if (failure) {
    return (
      <FailurePanel
        failure={failure as ApiError}
        personalHref={personalHref}
        onGoPersonal={onGoPersonal}
      />
    );
  }
  // `tiles`, because the first thing this Brief paints is the stat cards.
  if (!data) return <LoadingPanel shape="home" />;

  const generatedAt = new Date(data.generatedAt);
  const pendingReviewCount = props.pendingReviewCount === undefined
    ? data.reviewCounts.total
    : props.pendingReviewCount;
  const siteNameOf = (jobSiteId: string) =>
    data.sites.find((site: ConsoleSite) => site.id === jobSiteId)?.name || undefined;
  /** The Home page's project filter (`?site=`) — the topbar picker's state.
   *  Both queries already honoured it server-side; this is the render side. */
  const siteFilter = new URLSearchParams(search).get("site");
  const siteFilterName = siteFilter ? (siteNameOf(siteFilter) ?? siteFilter) : null;
  const personal = data.activeWorkspace.type === "personal";
  const line = headline(data);

  /** Defended against a payload that predates the field — see the old file's
   *  note: a `.map` on `undefined` here would take the whole page down. */
  const generated = data.generated ?? {
    byKind: (["log", "observation", "task", "punch"] as const).map((kind) => ({ kind, count: 0 })),
    total: 0,
  };
  const homeMentions = { fragment: activeFragment, choices: mentionResults.choices };
  const mentionOpen = !mentionDismissed && homeMentions.fragment !== null;
  const activeMentionIndex = Math.min(mentionIndex, Math.max(0, homeMentions.choices.length - 1));
  const displayedQuestion = completedMentionParts(homeQuestion, completedMentions);

  // ------------------------------------------------------------------
  // Where every number on this page goes (spec §2.1)
  // ------------------------------------------------------------------

  const peopleHref = (userId?: string) =>
    hrefWithAll(search, [
      ["item", null],
      ["page", "people"],
      ["person", userId ?? null],
    ]);
  const itemsHref = (view?: string) =>
    hrefWithAll(search, [
      ["item", null],
      ["page", "items"],
      ["view", view ?? null],
    ]);
  const walksHref = () =>
    hrefWithAll(search, [
      ["item", null],
      ["view", null],
      ["page", "walks"],
    ]);
  const askHref = (question?: string, submit = false) =>
    hrefWithAll(search, [
      ["item", null],
      ["view", null],
      ["page", "ask"],
      ["ask", question?.trim() || null],
      ["askChat", null],
      ["askSubmit", submit && question?.trim() ? "true" : null],
    ]);

  const completeMention = (mention: AskMention) => {
    const fragment = homeMentions.fragment;
    if (!fragment) return;
    const end = mentionCaret;
    const next = `${homeQuestion.slice(0, fragment.at)}@${mention.name} ${homeQuestion.slice(end)}`;
    const nextCaret = fragment.at + mention.name.length + 2;
    setHomeQuestion(next);
    setMentionCaret(nextCaret);
    setMentionIndex(0);
    setMentionDismissed(true);
    setCompletedMentions((current) =>
      current.includes(mention.name) ? current : [...current, mention.name],
    );
    // Returning focus after a pointer selection keeps a second mention only a
    // keystroke away; `requestAnimationFrame` waits for the controlled value.
    requestAnimationFrame(() => {
      homeAskInput.current?.focus();
      homeAskInput.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const setAskFilter = (key: "site" | "askRange", value: string | null) =>
    onNavigate(hrefWithAll(search, [[key, value]]));

  const adoptionLinks: AdoptionLinks = {
    peopleHref: peopleHref(),
    hrefForPerson: (userId: string) => peopleHref(userId),
    onOpenPeople: (userId?: string) => onNavigate(peopleHref(userId)),
    itemsHref: itemsHref(),
    onOpenItems: () => onNavigate(itemsHref()),
    walksHref: walksHref(),
    onOpenWalks: () => onNavigate(walksHref()),
    askHref: askHref(),
    onOpenAsk: () => onNavigate(askHref()),
    hrefForProject,
    onOpenProject,
  };

  // ------------------------------------------------------------------
  // LED-1151: the project tiles and recent walks
  // ------------------------------------------------------------------

  const inventory = props.inventory !== undefined ? props.inventory : inventoryResource.data;
  const allSites = inventory?.sites ?? data.sites;
  const siteName = (id: string) =>
    allSites.find((site) => site.id === id)?.name || siteNameOf(id) || "Untitled project";
  const cards = projectResource.data?.projects ?? [];
  const ordered = recentProjects(cards, allSites);
  const streakWalks = streakWalksResource.data?.walks ?? [];
  const walkCounts = new Map<string, number>();
  if (streakWalksResource.data && streakWalksResource.data.total <= streakWalks.length) {
    for (const site of allSites) walkCounts.set(site.id, 0);
    for (const walk of streakWalks) walkCounts.set(walk.jobSiteId, (walkCounts.get(walk.jobSiteId) ?? 0) + 1);
  }
  const pickerProjects: PickerProject[] = ordered.map((card) => ({
    id: card.id,
    name: card.name,
    syncSource: card.syncSource,
    lastWalkAt: card.lastWalkAt,
    toReview: card.toReview ?? 0,
  }));
  /** First run: a workspace with no projects at all. */
  const noProjects =
    data.sites.length === 0 && !!projectResource.data && cards.length === 0 && allSites.length === 0;
  const anim = animParity === 0 ? "a" : "b";
  const shareSearch = scopeSearch;
  const shareUrl = (() => {
    try {
      const url = new URL(location.href);
      url.search = hrefWithAll(scopeSearch, [["site", siteFilter]]);
      return url.toString();
    } catch {
      return location.href;
    }
  })();
  const pageGround = siteFilter ? H.mainScoped : H.mainAll;
  /** Who can see this project. A Personal workspace has no roster — it is
   *  just the reader, so the stack is just them. */
  const selfName = person?.name || data.caller?.name || "You";
  const selfEmail = person?.email ?? data.caller?.email;
  const shareMembers: OrgMemberRow[] = roster.data?.members?.length
    ? roster.data.members
    : [{ userId: data.userId, name: selfName, email: selfEmail, rawRole: "org:admin", role: "admin" }];

  return (
    <div style={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {/* Scoped: the way back out, pinned top-left of the main pane. */}
      {siteFilter && (
        <a
          href={hrefWithAll(search, [["site", null]])}
          className="kb press"
          data-testid="home-all-projects-pill"
          onClick={(event) => {
            event.preventDefault();
            setAskFilter("site", null);
          }}
          style={{
            position: "absolute",
            left: 28,
            top: 24,
            zIndex: 5,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            height: 38,
            padding: "0 14px 0 10px",
            borderRadius: 19,
            background: H.white,
            border: `1px solid ${H.edgeStrong}`,
            boxShadow: `0 2px 8px ${H.pillShadow}`,
            fontSize: 13.5,
            fontWeight: 700,
            color: H.ink,
            whiteSpace: "nowrap",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={H.ink} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="m15 6-6 6 6 6" />
          </svg>
          All projects
        </a>
      )}
      <div
        className="sb"
        data-testid="brief-body"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          // Anchor the composer to the viewport; the greeting sits above it.
          padding: `${HOME_TOP} 0 ${ASK_COMPOSER_CLEARANCE}px`,
          scrollbarGutter: "stable",
        }}
      >
        <div className="pagecol" style={WRAP}>
          {refreshFailure && <StaleBanner failure={refreshFailure} since={data.generatedAt} />}

          {/* ---------- head: the centred column ----------
              Lockup, greeting, the day and the workspace, then the one thing
              this page is for — asking. */}
          <div
            data-testid="brief-head"
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div className="home-hero-heading" style={{ position: "absolute", bottom: "100%", width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div className="home-hero-logo"><Lockup height={34} /></div>
              <h1
                key="greeting"
                className={`home-greet-${anim}`}
                style={{
                  margin: "26px 0 6px",
                  fontSize: 30,
                  fontWeight: 800,
                  letterSpacing: "-0.9px",
                  lineHeight: 1.1,
                  ...({ textWrap: "balance" } as React.CSSProperties),
                }}
                data-testid="brief-greeting"
              >
                {greeting(data, generatedAt, greetingNameFor(person, data.caller), siteFilterName)}
              </h1>
              <div className="home-hero-date" style={{ fontSize: 14, color: H.sub, marginBottom: 26 }}>
                {`${dayLabel(generatedAt)} · ${
                  data.activeWorkspace.type === "team" ? (data.activeWorkspace.name || "Team") : "Personal"
                }`}
              </div>

            </div>

            {/* The Home command bar is a real text input. Ask Led owns the
                actual request after the handoff; this surface owns the draft
                and the scope that travel there with it. */}
            <div
              data-testid="home-ask"
              style={{
                ...CHAT_INPUT_STYLE,
                height: "auto",
                minHeight: 62,
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                <AskTextInput
                  parts={displayedQuestion}
                  ref={homeAskInput}
                  // `askc-input` is carried for its one rule — the deliberate
                  // placeholder colour. Without it the prompt renders in the
                  // UA's own grey, which is a different grey from every other
                  // placeholder in the console.
                  className="askc-input"
                  data-testid="home-ask-input"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={mentionOpen}
                  aria-controls={mentionOpen ? "home-ask-mentions" : undefined}
                  aria-activedescendant={
                    mentionOpen ? `home-ask-mention-${activeMentionIndex}` : undefined
                  }
                  value={homeQuestion}
                  onInput={(event) => {
                    const input = event.currentTarget;
                    setHomeQuestion(input.value);
                    setMentionCaret(input.selectionStart ?? input.value.length);
                    setMentionIndex(0);
                    setMentionDismissed(false);
                    setCompletedMentions((current) =>
                      current.filter((name) => input.value.includes(`@${name}`)),
                    );
                  }}
                  onClick={(event) => {
                    const input = event.currentTarget;
                    setMentionCaret(input.selectionStart ?? input.value.length);
                    setMentionDismissed(false);
                  }}
                  onSelect={(event) => {
                    const input = event.currentTarget;
                    setMentionCaret(input.selectionStart ?? input.value.length);
                  }}
                  onKeyDown={(event) => {
                    if (mentionOpen && homeMentions.choices.length > 0 && event.key === "ArrowDown") {
                      event.preventDefault();
                      setMentionIndex((index) => (index + 1) % homeMentions.choices.length);
                      return;
                    }
                    if (mentionOpen && homeMentions.choices.length > 0 && event.key === "ArrowUp") {
                      event.preventDefault();
                      setMentionIndex((index) =>
                        (index - 1 + homeMentions.choices.length) % homeMentions.choices.length,
                      );
                      return;
                    }
                    if (mentionOpen && homeMentions.choices.length > 0 && (event.key === "Tab" || event.key === "Enter")) {
                      event.preventDefault();
                      completeMention(homeMentions.choices[activeMentionIndex]!);
                      return;
                    }
                    if (mentionOpen && event.key === "Escape") {
                      event.preventDefault();
                      setMentionDismissed(true);
                      return;
                    }
                    if (event.key === "Enter") {
                      event.preventDefault();
                      holdAskAttachment(askAttachment);
                      onNavigate(askHref(homeQuestion, true));
                    }
                  }}
                  placeholder="Ask Led AI a question, @ to name a project or sub"
                  // `ASK_TEXT` last of the text properties, and no `font`
                  // shorthand after it — see `ASK_TEXT` on the caret drift
                  // that cost.
                  style={{
                    width: "100%",
                    ...ASK_TEXT,
                    color: C.ink,
                    caretColor: C.ink,
                    outline: "none",
                    background: "transparent",
                    position: "relative",
                    zIndex: 1,
                  }}
                />
                {mentionOpen && (
                  <div
                    id="home-ask-mentions"
                    data-testid="home-ask-mentions"
                    role="listbox"
                    aria-label="Projects, companies, and people"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 12px)",
                      left: -8,
                      zIndex: 30,
                      width: "min(340px, calc(100vw - 56px))",
                      padding: 6,
                      border: `1px solid ${C.line2}`,
                      borderRadius: 14,
                      background: C.surface,
                      boxShadow: `0 12px 30px ${C.line}`,
                      textAlign: "left",
                    }}
                  >
                    <div style={{ ...META, padding: "6px 9px 7px", fontSize: 10.35, color: C.sub2 }}>
                      Mention a project, company, or person
                    </div>
                    {mentionResults.loading && <div role="status" style={{ padding: "6px 9px", color: C.sub }}>Searching directories…</div>}
                    {mentionResults.failed && <div role="alert" style={{ padding: "6px 9px", color: C.red }}>Some project directories could not be searched. Try again.</div>}
                    {!mentionResults.loading && !mentionResults.failed && !homeMentions.choices.length && <div style={{ padding: "6px 9px", color: C.sub }}>No matching entries.</div>}
                    <div style={{ maxHeight: 280, overflowY: "auto" }}>
                    {homeMentions.choices.map((mention, index) => (
                      <button
                        key={`${mention.kind}:${mention.id}`}
                        id={`home-ask-mention-${index}`}
                        type="button"
                        role="option"
                        aria-selected={index === activeMentionIndex}
                        className="kb"
                        data-testid="home-ask-mention"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => completeMention(mention)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          width: "100%",
                          minHeight: 38,
                          padding: "8px 10px",
                          border: 0,
                          borderRadius: 9,
                          background: index === activeMentionIndex ? C.railRow : "transparent",
                          color: C.ink,
                          cursor: "pointer",
                          font: "inherit",
                          textAlign: "left",
                        }}
                      >
                        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 15.52, fontWeight: 600 }}>
                          {mention.name}
                          {mention.detail && <span style={{ display: "block", fontSize: 12.65, fontWeight: 400, color: C.sub }}>{mention.detail}</span>}
                        </span>
                        <span style={{ ...META, flexShrink: 0, fontSize: 9.77, color: C.sub2 }}>
                          {mention.kind}
                        </span>
                      </button>
                    ))}
                    </div>
                    <div style={{ ...META, padding: "7px 9px 4px", fontSize: 9.77, color: C.sub2 }}>
                      ↑↓ to choose · Tab to complete
                    </div>
                  </div>
                )}
              </div>
              <DictationButton onTranscript={(text) => {
                setHomeQuestion((current) => `${current}${current && !/\s$/.test(current) ? " " : ""}${text}`);
                setMentionDismissed(true);
              }} />
              {/* No "Search" label. The bar's own placeholder says what it
                  is, and a word sitting between the field and its send button
                  read as a second, unclickable control. */}
              <button
                type="button"
                className="kb"
                data-testid="home-ask-send"
                onClick={() => {
                  // The attachment cannot ride in the URL, so it is handed
                  // over in memory and the Ask page takes it once.
                  holdAskAttachment(askAttachment);
                  onNavigate(askHref(homeQuestion, true));
                }}
                // A send button, not a label. The bar beside it already says
                // "Ask Led AI" as its placeholder, so the control repeating
                // the words was the page naming the same thing twice — and a
                // wide bordered pill read as a second field rather than as
                // the thing that submits the first. Round, ink, one arrow
                // (user-directed, 2026-09-07).
                aria-label="Ask Led AI"
                title="Ask Led AI"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  ...CHAT_SEND_STYLE,
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </button>
            </div>
            <HomeAskFilters
              sites={data.sites}
              activeSiteId={siteFilter}
              range={new URLSearchParams(search).get("askRange")}
              onSet={setAskFilter}
              attachment={askAttachment}
              attachmentError={askAttachmentError}
              onAttach={async (file) => {
                setAskAttachmentError(null);
                try {
                  setAskAttachment(await readAskAttachment(file));
                } catch (error) {
                  setAskAttachment(null);
                  setAskAttachmentError(
                    error instanceof AskAttachmentError
                      ? error.message
                      : "That file could not be read.",
                  );
                }
              }}
              onRemoveAttachment={() => {
                setAskAttachment(null);
                setAskAttachmentError(null);
              }}
              share={
                siteFilter ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <ShareAvatars members={shareMembers} ring={pageGround} />
                    <button
                      ref={shareButton}
                      type="button"
                      className="kb press"
                      data-testid="home-share"
                      aria-haspopup="dialog"
                      aria-expanded={shareOpen}
                      onClick={() => setShareOpen((open) => !open)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 32, padding: "0 6px", border: 0, borderRadius: 8, background: "transparent", font: "inherit", fontSize: 14, fontWeight: 700, color: H.ink, cursor: "pointer" }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={H.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M21 3 10 14" />
                        <path d="M21 3l-7 18-4-7-7-4 18-7Z" />
                      </svg>
                      Share
                    </button>
                  </span>
                ) : undefined
              }
            />

          </div>

          {/* ---------- below the header: everything else ----------
              First run is ONE centred block and nothing else. Otherwise: the
              project switcher (All projects only), the widget grid, and the
              recent walks — each exactly once. */}
          {noProjects ? (
            <HomeEmpty
              connectHref={personal ? hrefWithAll(search, [["page", "policies"]]) : peopleHref()}
              onNavigate={onNavigate}
            />
          ) : (
            <>
              {!siteFilter && (
                <HomeProjects
                  projects={pickerProjects}
                  recent={ordered.slice(0, 3)}
                  walkCounts={walkCounts}
                  activeSiteId={siteFilter}
                  hrefFor={(siteId) => hrefWithAll(search, [["site", siteId]])}
                  onPick={(siteId) => setAskFilter("site", siteId)}
                />
              )}
              {newProjectNote && !siteFilter && (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <NewProjectNote onClose={() => setNewProjectNote(false)} />
                </div>
              )}
              {/* Home summary cards are temporarily hidden in both project scopes. */}
              <HomeWalks
                walks={walksResource.data?.walks ?? []}
                total={walksResource.data?.total ?? 0}
                showProject={!siteFilter}
                siteName={siteName}
                allHref={walksHref()}
                hrefForWalk={hrefForWalk}
                onOpenWalk={onOpenWalk}
                onNavigate={onNavigate}
                animClass={`home-walks-${anim}`}
              />
            </>
          )}
        </div>
      </div>

      {shareOpen && siteFilter && (
        <SharePopover
          anchor={shareButton}
          projectName={siteFilterName ?? "this project"}
          search={shareSearch}
          roster={roster}
          callerEmail={selfEmail}
          self={shareMembers.length === 1 && !roster.data?.members?.length ? shareMembers[0] : undefined}
          shareUrl={shareUrl}
          onClose={() => setShareOpen(false)}
        />
      )}

      {nudgeItemId && (
        <NudgeSheet
          itemId={nudgeItemId}
          search={search}
          onClose={() => setNudgeItemId(null)}
        />
      )}
    </div>
  );
}
